/**
 * Marketing landing-page feature data (Phase 7 Pass 1 iter 266).
 *
 * Source of truth for the feature grid on `/`. Sourced from:
 *
 *   - `/product/market-and-gtm/positioning.md` § marketing claim hierarchy
 *   - `/CLAUDE.md` § Tech Stack
 *   - Phase 1 (discovery CLI), Phase 5 (MCP server), Phase 6 Pass 5
 *     (eval gate)
 *
 * Every card carries a SPECIFIC NUMBER per Pass-1 § acceptance criterion
 * "Features grid: 8 features with specific numbers (not adjectives)".
 * The tests assert the canonical numbers appear in the rendered grid.
 *
 * i18n: the user-facing `titleKey` / `descriptionKey` are `features.*` i18n
 * keys resolved in-component via `useT()`. `anchorMetric` is held verbatim ONLY
 * where it is genuinely language-independent — a code, a metric, a region, a
 * list of language codes. Anything holding a WORD carries an `anchorKey` as
 * well, and `anchor-localization.test.ts` enforces the distinction: five
 * anchors did not, so German readers saw "handelt als the signed-in user" and
 * "insgesamt 8 adapters" inside otherwise-translated prose.
 */

export interface FeatureCardData {
  /** Stable slug — used as the React key + the data-feature attribute. */
  readonly slug: string;
  /** i18n key for the short heading (1-4 words). */
  readonly titleKey: string;
  /** i18n key for the body copy — target 30-60 words. */
  readonly descriptionKey: string;
  /**
   * Concise numeric / proper-noun marker the test asserts on. Used to
   * keep the body copy honest — "specific numbers, not adjectives".
   * Held verbatim across locales (language-code lists, region names,
   * vendor names, metrics).
   */
  readonly anchorMetric: string;
  /**
   * I142: when an `anchorMetric` embeds translatable PROSE (e.g. "under 93 KB"),
   * this i18n key resolves the anchor per-locale (matching the title's localized
   * form) so the prose doesn't leak English into non-EN card bodies. The pure
   * numeric/code/name cores keep using the verbatim `anchorMetric` (no key).
   *
   * Required rather than optional in practice: `anchor-localization.test.ts`
   * refuses a metric containing a standalone lowercase word without one. It is
   * still `?:` because the majority of anchors — `RFC 9470`,
   * `EN · ES · FR · DE · AR · HE`, `api-witness` — correctly have none.
   */
  readonly anchorKey?: string;
}

export const FEATURES: readonly FeatureCardData[] = [
  {
    slug: 'auto-discovery',
    titleKey: 'features.card.autoDiscovery.title',
    descriptionKey: 'features.card.autoDiscovery.description',
    anchorMetric: '85% or better',
    anchorKey: 'features.card.autoDiscovery.anchor',
  },
  {
    slug: 'widget-plus-mcp',
    titleKey: 'features.card.widgetPlusMcp.title',
    descriptionKey: 'features.card.widgetPlusMcp.description',
    anchorMetric: 'MCP server',
    anchorKey: 'features.card.widgetPlusMcp.anchor',
  },
  {
    slug: 'multi-provider',
    titleKey: 'features.card.multiProvider.title',
    descriptionKey: 'features.card.multiProvider.description',
    anchorMetric: 'Anthropic, OpenAI, and AWS Bedrock-EU',
    anchorKey: 'features.card.multiProvider.anchor',
  },
  {
    slug: 'eu-residency',
    titleKey: 'features.card.euResidency.title',
    descriptionKey: 'features.card.euResidency.description',
    anchorMetric: 'EU (Frankfurt)',
  },
  {
    slug: 'six-languages-rtl',
    titleKey: 'features.card.sixLanguagesRtl.title',
    descriptionKey: 'features.card.sixLanguagesRtl.description',
    anchorMetric: 'EN · ES · FR · DE · AR · HE',
  },
  {
    slug: 'audit-trail',
    titleKey: 'features.card.auditTrail.title',
    descriptionKey: 'features.card.auditTrail.description',
    anchorMetric: 'CC6 (access) and CC8 (change)',
    anchorKey: 'features.card.auditTrail.anchor',
  },
  {
    slug: 'eval-on-promote',
    titleKey: 'features.card.evalOnPromote.title',
    descriptionKey: 'features.card.evalOnPromote.description',
    anchorMetric: '5 percentage points',
    anchorKey: 'features.card.evalOnPromote.anchor',
  },
  {
    slug: 'wcag-and-bundle',
    titleKey: 'features.card.wcagAndBundle.title',
    descriptionKey: 'features.card.wcagAndBundle.description',
    anchorMetric: 'under 30 KB',
    anchorKey: 'features.card.wcagAndBundle.anchor',
  },
  {
    slug: 'design-studio',
    titleKey: 'features.card.designStudio.title',
    descriptionKey: 'features.card.designStudio.description',
    anchorMetric: 'shadcn, MUI, Chakra, and Mantine',
    anchorKey: 'features.card.designStudio.anchor',
  },
  {
    slug: 'act-as-user',
    titleKey: 'features.card.actAsUser.title',
    descriptionKey: 'features.card.actAsUser.description',
    anchorMetric: 'the signed-in user',
    anchorKey: 'features.card.actAsUser.anchor',
  },
  {
    slug: 'identity-providers',
    titleKey: 'features.card.identityProviders.title',
    descriptionKey: 'features.card.identityProviders.description',
    anchorMetric: '12 adapters',
    anchorKey: 'features.card.identityProviders.anchor',
  },
  {
    slug: 'guarded-actions',
    titleKey: 'features.card.guardedActions.title',
    descriptionKey: 'features.card.guardedActions.description',
    anchorMetric: 'RFC 9470',
  },
  {
    slug: 'sdks',
    titleKey: 'features.card.sdks.title',
    descriptionKey: 'features.card.sdks.description',
    anchorMetric: '8 languages',
    anchorKey: 'features.card.sdks.anchor',
  },
  {
    slug: 'drift-detection',
    titleKey: 'features.card.driftDetection.title',
    descriptionKey: 'features.card.driftDetection.description',
    anchorMetric: 'api-witness',
  },
] as const;
